import { useEffect, useMemo, useRef, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { BootResponse, OfferAPIResponse, Preview } from '@appcharge/shared-ui';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import useOffersUI from 'api/useOffersUI';
import { useProduct } from 'api/useProduct';
import useSegments from 'api/useSegments';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import { Offer, OfferBadge } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { EToasterStatus, showToast } from 'utils/showToast';
import { TRANSLATE_MOCK_MAP } from 'views/Offers/Offer.consts';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import { z } from 'zod';

import { Product } from 'components/ProductsForm/ProductsForm.types';

import { parsePersonalizationJson } from '../TestPersonalization/defaults';
import { templateStudioSchema } from '../validation-schema';

import { generateBootInfo } from './utils/boot';
import {
  buildEnrichedOffersPayload,
  mergePersonalizationWithOffers
} from './utils/enrichedOffers';
import { generateOffersAPIResponse } from './utils/offers';
import { generatePlayerData } from './utils/playerData';
import { mapPreviewResponseToOfferAPIResponse } from './utils/previewResponse';

const PREVIEW_RESPONSE_STORAGE_KEY_PREFIX =
  'test_personalization_preview_response_';

function parsePreviewResponse(stored: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(stored) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export const usePreview = () => {
  const form = useFormContext<z.infer<typeof templateStudioSchema>>();
  const {
    previewDeviceLayout,
    displayPopupPreview,
    setDisplayPopupPreview,
    scrollToSection,
    personalizationJson,
    setGeneratedOffersJson,
    previewOffersResponse,
    setPreviewOffersResponse
  } = useTemplateStudioState();
  const { watch } = form;
  const { publisherId } = useParams();
  const { getStoreTheme } = useTheme(publisherId);
  const { getProducts } = useProduct(publisherId!);
  const { getTabs } = useTabs(publisherId);
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { getOffersUI } = useOffersUI(publisherId!);
  const { getSegments } = useSegments(publisherId);
  const navigate = useNavigate();

  useEffect(() => {
    if (!publisherId || previewOffersResponse !== undefined) return;
    const key = `${PREVIEW_RESPONSE_STORAGE_KEY_PREFIX}${publisherId}`;
    const stored = sessionStorage.getItem(key);
    if (!stored) return;
    const parsed = parsePreviewResponse(stored);
    if (parsed) {
      setPreviewOffersResponse(parsed);
    } else {
      sessionStorage.removeItem(key);
    }
  }, [publisherId, previewOffersResponse, setPreviewOffersResponse]);

  const { getOffers: getPromotions } = useOffers({
    offerType: OfferType.SPECIAL_OFFER,
    fetchOnlyActiveOffers: true,
    shouldIgnorePagination: true
  });

  const { fetchAllOffers } = useOffers({});

  const [realOffersMap, setRealOffersMap] = useState<{
    bundles: Offer[];
    promotions: Offer[];
    progressBars: Offer[];
    rolling: Offer[];
    popups: Offer[];
  } | null>(null);

  const [realOffersLoadAttempted, setRealOffersLoadAttempted] = useState(false);
  const realOffersLoadedForPublisherRef = useRef<string | null>(null);

  useEffect(() => {
    if (!publisherId) {
      realOffersLoadedForPublisherRef.current = null;
      return;
    }
    if (realOffersLoadedForPublisherRef.current === publisherId) return;
    realOffersLoadedForPublisherRef.current = publisherId;
    setRealOffersLoadAttempted(false);
    const loadRealOffers = async () => {
      try {
        const [
          bundleOffers,
          specialOffers,
          progressBarOffers,
          rollingOffers,
          popupOffers
        ] = await Promise.all([
          fetchAllOffers({
            offerType: OfferType.BUNDLE,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.SPECIAL_OFFER,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.PROGRESS_BAR,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.ROLLING_OFFER,
            fetchOnlyActiveOffers: true
          }),
          fetchAllOffers({
            offerType: OfferType.POPUP,
            fetchOnlyActiveOffers: true
          })
        ]);
        setRealOffersMap({
          bundles: bundleOffers,
          promotions: specialOffers,
          progressBars: progressBarOffers,
          rolling: rollingOffers,
          popups: popupOffers
        });
      } catch (error) {
        console.error('Failed to load real offers for personalization:', error);
        setRealOffersMap(null);
      } finally {
        setRealOffersLoadAttempted(true);
      }
    };
    loadRealOffers();
  }, [publisherId, fetchAllOffers]);

  const formValues = watch();
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  const [formVersion, setFormVersion] = useState(0);
  const formValuesSignatureRef = useRef<string>('');

  useEffect(() => {
    const signature = JSON.stringify(formValues);
    if (signature !== formValuesSignatureRef.current) {
      formValuesSignatureRef.current = signature;
      setFormVersion((v) => v + 1);
    }
  }, [formValues]);

  const boot: BootResponse = useMemo(() => {
    if (!getStoreTheme.data) return;

    return generateBootInfo(
      formValuesRef.current,
      getStoreTheme.data,
      previewDeviceLayout,
      featureFlags
    );
  }, [formVersion, getStoreTheme.data, previewDeviceLayout, featureFlags]);

  const defaultOffers = useMemo(() => {
    if (!boot || !getProducts.data?.result || !getOffersUI.data?.result) return;

    const realPromotionBadges: OfferBadge[] =
      getPromotions?.data?.offers?.flatMap(
        (offer: Offer) => offer.badges || []
      ) || [];

    if (getProducts.data.result.length === 0) {
      navigate('../');
      showToast({
        message: 'Please add at least one product to the dashboard',
        status: EToasterStatus.ERROR
      });
      return;
    }

    if (getOffersUI.data?.result.length === 0) {
      navigate('../');
      showToast({
        message: 'Please add offer designs to the dashboard',
        status: EToasterStatus.ERROR
      });
      return;
    }

    const products: Product[] = [...getProducts.data?.result];

    if (products.length > 0 && products.length < 10) {
      const lastProduct = products[products.length - 1];
      while (products.length < 10) {
        products.push({ ...lastProduct });
      }
    }

    return generateOffersAPIResponse({
      formValues: formValuesRef.current,
      boot,
      products: products,
      offerUIS: getOffersUI.data?.result,
      promotionBadges: realPromotionBadges,
      tabs: getTabs.data || [],
      navigate
    });
  }, [
    formVersion,
    boot,
    getProducts.data?.result,
    getOffersUI.data?.result,
    getPromotions?.data?.offers,
    getTabs.data
  ]);

  const offersForPersonalization: OfferAPIResponse | undefined = useMemo(() => {
    if (!defaultOffers) return;
    const segments = Array.isArray(getSegments?.data) ? getSegments.data : [];
    const base = { ...defaultOffers, segments };
    if (!realOffersMap) return base;

    return {
      ...base,
      offers: {
        ...defaultOffers.offers,
        bundles: realOffersMap.bundles,
        promotions: realOffersMap.promotions,
        progressBars: realOffersMap.progressBars,
        rolling: realOffersMap.rolling,
        popups: realOffersMap.popups
      }
    };
  }, [defaultOffers, realOffersMap, getSegments?.data]);

  const lastGeneratedJsonRef = useRef<string>('');

  useEffect(() => {
    if (!offersForPersonalization || !boot || !realOffersLoadAttempted) return;

    const enrichedOffers = buildEnrichedOffersPayload(
      boot,
      offersForPersonalization
    );
    const nextJson = JSON.stringify(enrichedOffers, null, 2);
    if (nextJson !== lastGeneratedJsonRef.current) {
      lastGeneratedJsonRef.current = nextJson;
      setGeneratedOffersJson(nextJson);
    }
  }, [
    offersForPersonalization,
    setGeneratedOffersJson,
    boot,
    realOffersLoadAttempted
  ]);

  const offers: OfferAPIResponse | undefined = useMemo(() => {
    let result: OfferAPIResponse | undefined;
    const isFromApiPreview = !!previewOffersResponse;
    if (previewOffersResponse) {
      result = mapPreviewResponseToOfferAPIResponse(previewOffersResponse);
    } else {
      const parsedPersonalization =
        parsePersonalizationJson(personalizationJson);
      if (parsedPersonalization && defaultOffers) {
        result = mergePersonalizationWithOffers(
          parsedPersonalization,
          defaultOffers
        );
      } else {
        result = defaultOffers;
      }
    }
    if (result && !isFromApiPreview) {
      result = { ...result, tabs: getTabs.data ?? [] };
    }
    const isPersonalizationPreview =
      !!previewOffersResponse ||
      !!(personalizationJson && parsePersonalizationJson(personalizationJson));
    const formProgressBarDesignId =
      boot?.storeTheme?.general?.progressBarInternalViewModel;
    const offerUIs = getOffersUI.data?.result ?? [];
    const formProgressBarOfferUI = formProgressBarDesignId
      ? offerUIs.find(
          (ui: any) =>
            ui.offerUiType === OfferDesignTypes.ProgressBar &&
            ui.externalId === formProgressBarDesignId
        ) ??
        offerUIs.find(
          (ui: any) => ui.offerUiType === OfferDesignTypes.ProgressBar
        )
      : null;

    if (
      isPersonalizationPreview &&
      !isFromApiPreview &&
      result?.offers?.progressBars?.length &&
      formProgressBarDesignId &&
      formProgressBarOfferUI
    ) {
      result = {
        ...result,
        offers: {
          ...result.offers,
          progressBars: result.offers.progressBars.map((pb: any) => {
            const offerUiId = formProgressBarDesignId;
            const offerUi = {
              ...formProgressBarOfferUI,
              offerUiId,
              externalId: formProgressBarDesignId
            };
            return {
              ...pb,
              offerUiId,
              offerUi
            };
          })
        }
      };
    }

    const formPromotionDesignId =
      boot?.storeTheme?.general?.specialOffersInternalViewModel;
    const formPromotionOfferUI = formPromotionDesignId
      ? offerUIs.find(
          (ui: any) =>
            ui.offerUiType === OfferDesignTypes.SpecialOffer &&
            ui.externalId === formPromotionDesignId
        ) ??
        offerUIs.find(
          (ui: any) => ui.offerUiType === OfferDesignTypes.SpecialOffer
        )
      : null;

    if (
      isPersonalizationPreview &&
      !isFromApiPreview &&
      result?.offers?.promotions?.length &&
      formPromotionDesignId
    ) {
      result = {
        ...result,
        offers: {
          ...result.offers,
          promotions: result.offers.promotions.map(
            (promo: any, index: number) => {
              const offerUiId = formPromotionDesignId;
              const offerUi = formPromotionOfferUI
                ? {
                    ...formPromotionOfferUI,
                    offerUiId,
                    externalId: formPromotionDesignId
                  }
                : promo.offerUi
                  ? {
                      ...promo.offerUi,
                      offerUiId,
                      externalId: formPromotionDesignId
                    }
                  : { offerUiId, externalId: formPromotionDesignId };
              const badgesFromGenerated =
                defaultOffers?.offers?.promotions?.[index]?.badges;
              const badges = Array.isArray(badgesFromGenerated)
                ? badgesFromGenerated
                : promo.badges ?? [];
              return {
                ...promo,
                offerUiId,
                offerUi,
                badges
              };
            }
          )
        }
      };
    }

    if (result && boot?.storeTheme?.general && !isFromApiPreview) {
      const general = boot.storeTheme.general;
      const formLogo =
        general.logo != null && String(general.logo).trim() !== ''
          ? general.logo
          : undefined;
      result = {
        ...result,
        logo: formLogo ?? result.logo
      } as OfferAPIResponse;
      if (typeof (result as any).storeTheme === 'object') {
        (result as any).storeTheme = {
          ...(result as any).storeTheme,
          logo: general.logo,
          bgImageMobile: general.backgroundImageMobile,
          bgImageDesktop: general.backgroundImageDesktop
        };
      }
    }

    return result;
  }, [
    defaultOffers,
    personalizationJson,
    previewOffersResponse,
    getTabs.data,
    boot,
    getOffersUI.data?.result
  ]);

  const bootForPreview: BootResponse | undefined = boot;

  const previewProps: React.ComponentProps<typeof Preview> = useMemo(
    () => ({
      boot: bootForPreview,
      playerData: generatePlayerData(),
      device: previewDeviceLayout,
      translate: (key: string) => TRANSLATE_MOCK_MAP[key] || key,
      offers,
      displayPopup: displayPopupPreview,
      onClosePopup: () => setDisplayPopupPreview(false),
      scrollToSection
    }),
    [
      bootForPreview,
      offers,
      previewDeviceLayout,
      displayPopupPreview,
      setDisplayPopupPreview,
      scrollToSection
    ]
  );

  return { previewProps };
};
